import {
  getApprovalClasses,
  getApprovalLabel,
  type FolderApprovalProgressSummary,
} from '@/entities/project-folder';

interface Props {
  progress: FolderApprovalProgressSummary;
  /**
   * `count` is the rail's compact `3/5`; `label` is the fuller "3 of 5
   * approved" the cards and the pane header have room for. The two differ in
   * box as well as text — see the border below.
   */
  display?: 'count' | 'label';
  className?: string;
}

/**
 * A folder's approval standing, wherever a folder is drawn.
 *
 * Renders nothing for a folder with no assets: there is no progress to report
 * yet, and the design leaves the slot empty rather than showing `0/0`. Keeping
 * that rule here is the point of the component — it was written out three times
 * and the copies had already drifted apart.
 */
export function ApprovalProgressBadge(props: Props) {
  const { progress } = props;
  if (progress.totalCount === 0) return null;

  const { bg, text } = getApprovalClasses(progress);
  // The card pill matches the design's 22px box, which needs the border to
  // reach it. The rail cannot afford those 2px: its rows are a fixed 36px and
  // the badge is the tallest thing in one, so a bordered badge grows only the
  // rows that have one and the tree stops sitting on an even rhythm.
  const box = props.display === 'count' ? '' : 'border border-transparent';

  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-0.5 text-body-sm font-medium ${box} ${bg} ${text} ${props.className ?? ''}`}
    >
      {props.display === 'count'
        ? `${progress.approvedCount}/${progress.totalCount}`
        : getApprovalLabel(progress)}
    </span>
  );
}
